/**
 * MCP Skill 卡片组件（完全独立）
 *
 * 与工具卡片完全分离：
 * - 独立的组件
 * - 独立的样式
 * - 独立的数据结构
 * - 不影响工具卡片功能
 */

import React, { useState } from 'react';
import { Star, Download, ExternalLink, Copy, Check, Package, Tag, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { Skill, getRatingStars, getInstallCommand } from '../api/skillApi';

interface SkillCardProps {
  skill: Skill;
  rank?: number;
  onClick?: () => void;
  lang?: 'zh' | 'en';
  expanded?: boolean; // 是否展开显示详情
}

export default function SkillCard({ skill, rank, onClick, lang = 'zh', expanded = false }: SkillCardProps) {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleCopyCommand = (e: React.MouseEvent) => {
    e.stopPropagation();
    const command = getInstallCommand(skill);
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const installCommand = getInstallCommand(skill);

  const handleClick = () => {
    if (onClick) {
      // 如果有 onClick 回调，直接调用（跳转到详情页）
      onClick();
    } else {
      // 否则展开/收起详情
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="group relative bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer border border-purple-200 dark:border-purple-700 overflow-hidden"
    >
      {/* 头部 */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {rank && (
                <span className="flex items-center justify-center w-6 h-6 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm rounded-full font-bold">
                  {rank}
                </span>
              )}
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                {skill.name}
              </h3>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {skill.rating}
                </span>
              </div>
            </div>

            {/* 发布者 */}
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              by <span className="font-medium text-purple-600 dark:text-purple-400">{skill.publisher}</span>
            </div>
          </div>

          <a
            href={skill.homepage}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="查看主页"
          >
            <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
          </a>
        </div>

        {/* 描述 */}
        <p className="text-gray-700 dark:text-gray-300 text-sm mb-3 line-clamp-2 leading-relaxed">
          {skill.description}
        </p>

        {/* 分类标签 */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {skill.categories.slice(0, 3).map((category, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full"
            >
              <Tag className="w-3 h-3" />
              {category}
            </span>
          ))}
        </div>

        {/* 标签 */}
        {skill.tags && skill.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {skill.tags.slice(0, 4).map((tag, index) => (
              <span
                key={index}
                className="text-xs text-gray-500 dark:text-gray-400"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* 安装命令 */}
        <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between">
            <code className="text-xs text-green-400 font-mono truncate flex-1 mr-2">
              {installCommand}
            </code>
            <button
              onClick={handleCopyCommand}
              className="p-1.5 hover:bg-gray-800 dark:hover:bg-gray-800 rounded transition-colors flex-shrink-0"
              title={copied ? '已复制' : '复制命令'}
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* 底部信息 */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 border-t border-purple-200 dark:border-purple-800 pt-3">
          <div className="flex items-center gap-3">
            {/* 下载量 */}
            <div className="flex items-center gap-1">
              <Download className="w-3.5 h-3.5" />
              <span>{skill.downloads}</span>
            </div>

            {/* 许可证 */}
            <div className="flex items-center gap-1">
              <Package className="w-3.5 h-3.5" />
              <span>{skill.license}</span>
            </div>
          </div>

          {/* 评分星星 */}
          <div className="text-sm">
            {getRatingStars(skill.rating)}
          </div>
        </div>
      </div>

      {/* 可展开的详情部分 - 只在没有 onClick 时显示 */}
      {!onClick && skill.useCases && skill.useCases.length > 0 && (
        <>
          {/* 展开/收起按钮 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="w-full px-5 py-2 flex items-center justify-center gap-2 text-xs text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors border-t border-purple-200 dark:border-purple-800"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                {lang === 'zh' ? '收起详情' : 'Show Less'}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                {lang === 'zh' ? '查看详情' : 'View Details'}
              </>
            )}
          </button>

          {/* 详情内容 */}
          {isExpanded && (
            <div className="px-5 pb-5 bg-purple-50/50 dark:bg-purple-900/10 border-t border-purple-200 dark:border-purple-700">
              {/* 使用场景 */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {lang === 'zh' ? '使用场景' : 'Use Cases'}
                  </h4>
                </div>
                <ul className="space-y-1.5">
                  {skill.useCases.map((useCase, index) => (
                    <li key={index} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <span>{useCase}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 快速开始 */}
              {skill.quickStart && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-purple-600 dark:text-purple-400">⚡</span>
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {lang === 'zh' ? '快速开始' : 'Quick Start'}
                    </h4>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    {skill.quickStart}
                  </p>
                </div>
              )}

              {/* 仓库链接 */}
              <div className="mt-4 flex gap-2">
                <a
                  href={skill.repository}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lang === 'zh' ? '查看仓库' : 'View Repository'}
                </a>
                <a
                  href={skill.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-lg transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lang === 'zh' ? '文档' : 'Docs'}
                </a>
              </div>
            </div>
          )}
        </>
      )}

      {/* 装饰性渐变边框 */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none -z-10 blur-sm" />
    </div>
  );
}
